import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Brain, Heart, IndianRupee, Briefcase, Home, Sparkles, Star, User,
} from 'lucide-react';

// 8 life dimensions around the central figure
const NODES = [
  { key: 'physical',      label: 'Physical',    icon: Activity },
  { key: 'mental',        label: 'Mental',      icon: Brain },
  { key: 'relationships', label: 'Relations',   icon: Heart },
  { key: 'finance',       label: 'Finance',     icon: IndianRupee },
  { key: 'career',        label: 'Career',      icon: Briefcase },
  { key: 'lifestyle',     label: 'Lifestyle',   icon: Home },
  { key: 'purpose',       label: 'Purpose',     icon: Star },
  { key: 'self_care',     label: 'Self-Care',   icon: Sparkles },
];

// person moves toward the GREEN (over-focused) dimension each scenario
const SCENARIOS = [
  { green: 'career',   red: 'physical',      greenText: 'Successful Career', redText: 'Exhausted Body' },
  { green: 'finance',  red: 'relationships', greenText: 'Good Income',        redText: 'Poor Relationships' },
  { green: 'mental',   red: 'purpose',       greenText: 'More Knowledge',     redText: 'Less Direction' },
  { green: 'physical', red: 'finance',       greenText: 'Fit Body',           redText: 'Financial Stress' },
  { all: true,         caption: 'Life Works Better When Everything Works Together' },
];

const GREEN = '#10B981';
const RED = '#EF4444';
const INDIGO = '#5B5CEB';
const MUTED = '#CBD5E1';

export default function HeroVisualization() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % SCENARIOS.length), 3500);
    return () => clearInterval(id);
  }, []);
  const scenario = SCENARIOS[index];

  // Layout 8 nodes evenly around a circle
  const layout = useMemo(() => {
    const radius = 140;
    return NODES.map((n, i) => {
      const angle = -Math.PI / 2 + (2 * Math.PI * i) / NODES.length;
      return {
        ...n,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      };
    });
  }, []);

  const nodeColor = (key) => {
    if (scenario.all) return INDIGO;
    if (key === scenario.green) return GREEN;
    if (key === scenario.red) return RED;
    return MUTED;
  };

  // Person target position — moves toward the GREEN node, else center
  const personTarget = useMemo(() => {
    if (scenario.all || !scenario.green) return { x: 0, y: 0 };
    const node = layout.find((n) => n.key === scenario.green);
    if (!node) return { x: 0, y: 0 };
    // Move ~55% of the way toward the green node
    return { x: node.x * 0.55, y: node.y * 0.55 };
  }, [scenario, layout]);

  return (
    <div className="relative w-full max-w-[440px] mx-auto" data-testid="hero-visualization">
      <div className="relative aspect-square">
        {/* Connecting lines (SVG) — light up indigo on balanced state */}
        <svg viewBox="-200 -200 400 400" className="absolute inset-0 w-full h-full">
          {layout.map((n) => (
            <line
              key={n.key}
              x1="0" y1="0" x2={n.x} y2={n.y}
              stroke={scenario.all ? INDIGO : '#E5E7EB'}
              strokeWidth={scenario.all ? 1.5 : 0.8}
              strokeDasharray={scenario.all ? '0' : '3 4'}
              style={{ transition: 'stroke 600ms ease, stroke-width 600ms ease' }}
            />
          ))}
          {/* Outer ring on the balanced scenario */}
          <motion.circle
            cx="0" cy="0" r="140"
            fill="none"
            stroke={INDIGO}
            strokeWidth="2"
            strokeDasharray="900"
            initial={{ strokeDashoffset: 900, opacity: 0 }}
            animate={{
              strokeDashoffset: scenario.all ? 0 : 900,
              opacity: scenario.all ? 1 : 0,
            }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
          />
        </svg>

        {/* Center / moving person */}
        <motion.div
          className="absolute left-1/2 top-1/2 z-20"
          animate={{ x: personTarget.x - 32, y: personTarget.y - 32 }}
          transition={{ type: 'spring', stiffness: 80, damping: 16, duration: 1.0 }}
          style={{ width: 64, height: 64 }}
        >
          <motion.div
            className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl"
            animate={{
              rotate: scenario.all ? 0 : [-2, 2, -2],
              scale: scenario.all ? 1.08 : 1,
            }}
            transition={{
              rotate: { repeat: scenario.all ? 0 : Infinity, duration: 1.4 },
              scale: { duration: 0.8, ease: 'easeOut' },
            }}
            style={{
              background: scenario.all
                ? `radial-gradient(circle at 30% 30%, ${INDIGO}, #4B4CD9)`
                : 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
              boxShadow: scenario.all
                ? `0 0 40px ${INDIGO}66`
                : '0 12px 30px rgba(0,0,0,0.18)',
              transition: 'background 800ms ease, box-shadow 800ms ease',
            }}
          >
            <User className="w-8 h-8 text-white" />
          </motion.div>
        </motion.div>

        {/* Dimension nodes */}
        {layout.map((n) => {
          const color = nodeColor(n.key);
          const isRed = !scenario.all && scenario.red === n.key;
          const isGreen = !scenario.all && scenario.green === n.key;
          const muted = !scenario.all && !isRed && !isGreen;
          const Icon = n.icon;
          return (
            <motion.div
              key={n.key}
              className="absolute z-10"
              style={{
                left: `calc(50% + ${n.x}px - 28px)`,
                top: `calc(50% + ${n.y}px - 28px)`,
              }}
              animate={{
                x: isRed ? [0, -2, 2, -2, 2, 0] : 0,
                scale: scenario.all ? 1.05 : isGreen ? 1.12 : muted ? 0.92 : 1,
                opacity: muted ? 0.45 : 1,
              }}
              transition={{
                x: { duration: 0.4, repeat: isRed ? Infinity : 0 },
                scale: { duration: 0.6 },
                opacity: { duration: 0.6 },
              }}
            >
              <div
                className="w-14 h-14 rounded-full bg-white border-2 flex items-center justify-center transition-all duration-500"
                style={{
                  borderColor: color,
                  boxShadow: isGreen
                    ? `0 0 24px ${GREEN}80`
                    : isRed
                      ? `0 0 16px ${RED}55`
                      : scenario.all
                        ? `0 0 18px ${INDIGO}55`
                        : '0 4px 12px rgba(0,0,0,0.06)',
                }}
              >
                <Icon className="w-6 h-6" style={{ color, transition: 'color 500ms' }} />
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold whitespace-nowrap"
                   style={{ color: muted ? '#94A3B8' : '#475569' }}>
                {n.label}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Scenario caption */}
      <div className="mt-10 min-h-[80px] text-center" data-testid="hero-scenario-text">
        <AnimatePresence mode="wait">
          {scenario.all ? (
            <motion.div
              key="balanced"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-xl md:text-2xl font-bold" style={{ color: INDIGO, fontFamily: 'Outfit, sans-serif' }}>
                {scenario.caption}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="space-y-1.5"
            >
              <div className="text-lg md:text-xl font-semibold flex items-center justify-center gap-2"
                   style={{ color: GREEN, fontFamily: 'Outfit, sans-serif' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: GREEN }} />
                {scenario.greenText}
              </div>
              <div className="text-lg md:text-xl font-semibold flex items-center justify-center gap-2"
                   style={{ color: RED, fontFamily: 'Outfit, sans-serif' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: RED }} />
                {scenario.redText}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
